import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import {
  Activity,
  Loader2,
  Users,
  CheckCircle,
  XCircle,
  DollarSign,
  UserCheck,
  Calendar,
  MessageSquare,
  Mail,
  Package,
  Building2,
  Ticket,
  ChevronLeft,
  ChevronRight,
  Filter,
} from "lucide-react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const activityIcons = {
  booking_created: Users,
  booking_confirmed: CheckCircle,
  booking_cancelled: XCircle,
  payment_received: DollarSign,
  check_in: UserCheck,
  private_session: Calendar,
  session_confirmed: CheckCircle,
  session_declined: XCircle,
  support_ticket: MessageSquare,
  campaign_sent: Mail,
  class_created: Calendar,
  schedule_created: Calendar,
  package_purchased: Package,
  studio_booking: Building2,
  event_booking: Ticket,
};

const activityColors = {
  booking_created: "text-blue-400",
  booking_confirmed: "text-green-400",
  booking_cancelled: "text-red-400",
  payment_received: "text-green-400",
  check_in: "text-empire-gold",
  private_session: "text-purple-400",
  session_confirmed: "text-green-400",
  session_declined: "text-red-400",
  support_ticket: "text-yellow-400",
  campaign_sent: "text-blue-400",
  class_created: "text-purple-400",
  schedule_created: "text-purple-400",
  package_purchased: "text-empire-gold",
  studio_booking: "text-cyan-400",
  event_booking: "text-pink-400",
};

export const AdminActivities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const limit = 20;

  useEffect(() => {
    fetchActivities();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter]);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * limit;
      let url = `${API}/admin/activities?limit=${limit}&skip=${skip}`;
      if (filter !== "all") {
        url += `&activity_type=${filter}`;
      }
      const res = await axios.get(url);
      setActivities(res.data.activities);
      setTotal(res.data.total);
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  const activityTypes = [
    { value: "all", label: "All Activities" },
    { value: "booking_created", label: "Bookings Created" },
    { value: "booking_confirmed", label: "Bookings Confirmed" },
    { value: "booking_cancelled", label: "Bookings Cancelled" },
    { value: "payment_received", label: "Payments Received" },
    { value: "check_in", label: "Check-Ins" },
    { value: "private_session", label: "Private Sessions" },
    { value: "support_ticket", label: "Support Tickets" },
    { value: "campaign_sent", label: "Campaigns Sent" },
    { value: "package_purchased", label: "Package Purchases" },
    { value: "studio_booking", label: "Studio Bookings" },
    { value: "event_booking", label: "Event Bookings" },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Activity Log</h1>
          <p className="text-neutral-400 text-sm mt-1">Track all system activities and events</p>
        </div>
        <div className="flex items-center gap-3">
          <Filter className="w-4 h-4 text-neutral-400" />
          <Select value={filter} onValueChange={(value) => { setFilter(value); setPage(1); }}>
            <SelectTrigger className="w-[180px] bg-neutral-900 border-neutral-700">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent className="bg-neutral-900 border-neutral-700">
              {activityTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Activities List */}
      <Card className="card-empire">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-empire-gold" />
              Activities
            </span>
            <Badge variant="outline" className="border-neutral-700 text-neutral-400">
              {total} total
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-empire-gold animate-spin" />
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No activities found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activities.map((activity, index) => {
                const Icon = activityIcons[activity.activity_type] || Activity;
                const colorClass = activityColors[activity.activity_type] || "text-empire-gold";
                return (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="flex items-start gap-4 p-4 bg-neutral-900/50 rounded-lg border border-neutral-800"
                  >
                    <div className={`w-10 h-10 rounded-lg bg-neutral-800 flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-5 h-5 ${colorClass}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <p className="text-sm text-white font-medium">{activity.title}</p>
                        <span className="text-xs text-neutral-500 whitespace-nowrap">
                          {activity.created_at && format(parseISO(activity.created_at), "MMM d, yyyy 'at' h:mm a")}
                        </span>
                      </div>
                      {activity.description && (
                        <p className="text-sm text-neutral-400 mt-1">{activity.description}</p>
                      )}
                      {activity.booking && (
                        <div className="mt-2 p-2.5 bg-neutral-800/50 rounded-md border border-neutral-800">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="font-mono text-xs text-empire-gold">
                              {activity.booking.booking_number}
                            </span>
                            {activity.booking.item_label && (
                              <>
                                <span className="text-neutral-600">·</span>
                                <span className="text-sm text-white">
                                  {activity.booking.item_label}
                                </span>
                              </>
                            )}
                            {activity.booking.item_kind && (
                              <Badge
                                variant="outline"
                                className="text-[10px] border-neutral-700 text-neutral-400"
                              >
                                {activity.booking.item_kind}
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 mt-1 text-xs text-neutral-500">
                            {activity.booking.item_datetime_eat && (
                              <span>{activity.booking.item_datetime_eat}</span>
                            )}
                            {activity.booking.total_amount != null && (
                              <span>KES {activity.booking.total_amount?.toLocaleString()}</span>
                            )}
                            {activity.booking.mpesa_receipt_number && (
                              <span className="font-mono">
                                M-Pesa {activity.booking.mpesa_receipt_number}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <Badge
                          variant="outline"
                          className="text-xs border-neutral-700 text-neutral-400"
                        >
                          {activity.activity_type?.replace(/_/g, " ")}
                        </Badge>
                        {activity.reference_id && (
                          <span className="text-xs text-neutral-500">
                            Ref: {activity.reference_id}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-neutral-800">
              <p className="text-sm text-neutral-400">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="border-neutral-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="border-neutral-700"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
